import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DatePicker from './DatePicker';
import TimePicker from './TimePicker';
import {
  DEFAULT_DATA_EXPORT,
  EXPORT_ITEM_KEYS,
  EXPORT_ITEM_LABELS,
  EXPORT_ITEM_DESCRIPTIONS,
  normalizeDataExportSettings,
} from '../utils/dataExportSettings';

const getToken = () => localStorage.getItem('token');

function getMskTodayDate() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return new Date(
    parseInt(get('year'), 10),
    parseInt(get('month'), 10) - 1,
    parseInt(get('day'), 10),
  );
}

/** ISO UTC → дата (локальный Date без TZ) + HH:MM в МСК */
function fromIsoMsk(iso) {
  if (!iso) return { date: null, time: '12:00' };
  let str = String(iso).trim();
  if (!/Z|[+-]\d{2}:?\d{2}$/.test(str)) {
    str = `${str.replace(' ', 'T')}Z`;
  }
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return { date: null, time: '12:00' };

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);

  const get = (type) => parts.find((p) => p.type === type)?.value;
  const year = parseInt(get('year'), 10);
  const month = parseInt(get('month'), 10);
  const day = parseInt(get('day'), 10);
  let hour = parseInt(get('hour'), 10);
  let minute = parseInt(get('minute'), 10);
  if (hour === 24) hour = 0;
  minute = Math.round(minute / 5) * 5;
  if (minute === 60) minute = 55;

  return {
    date: new Date(year, month - 1, day),
    time: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
  };
}

/** Локальная календарная дата + HH:MM (как МСК) → ISO UTC */
function toIsoMsk(date, time) {
  if (!date || !time) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(time).trim());
  if (!m) return null;
  const hours = Math.min(23, Math.max(0, parseInt(m[1], 10)));
  let minutes = Math.min(59, Math.max(0, parseInt(m[2], 10)));
  minutes = Math.round(minutes / 5) * 5;
  if (minutes === 60) minutes = 55;
  const y = date.getFullYear();
  const mo = date.getMonth() + 1;
  const day = date.getDate();
  const msk = `${y}-${String(mo).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00+03:00`;
  const parsed = new Date(msk);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCountdown(nextIso) {
  if (!nextIso) return null;
  const next = new Date(nextIso).getTime();
  const now = Date.now();
  if (now >= next) return 'сейчас';
  const d = Math.floor((next - now) / (24 * 60 * 60 * 1000));
  const h = Math.floor(((next - now) % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  if (d > 0) return `через ${d} д. ${h} ч.`;
  if (h > 0) return `через ${h} ч.`;
  const m = Math.floor(((next - now) % (60 * 60 * 1000)) / (60 * 1000));
  return `через ${m} мин.`;
}

function SettingsToggle({ label, description, checked, onChange }) {
  return (
    <label className="mrp-rating-toggle">
      <input type="checkbox" className="mrp-rating-toggle-input" checked={checked} onChange={onChange} />
      <span className="mrp-rating-toggle-track" aria-hidden="true">
        <span className="mrp-rating-toggle-thumb" />
      </span>
      <span className="mrp-rating-toggle-text">
        <span className="mrp-rating-toggle-label">{label}</span>
        {description ? <span className="mrp-rating-toggle-desc">{description}</span> : null}
      </span>
    </label>
  );
}

export default function SettingsDataExport({ isActive, setError, setSuccess, userRoles = [] }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [form, setForm] = useState(() => normalizeDataExportSettings(DEFAULT_DATA_EXPORT));

  const isAdmin = userRoles.includes('admin');
  const scheduleParts = useMemo(() => fromIsoMsk(form.next_export_at), [form.next_export_at]);
  const minDate = useMemo(() => getMskTodayDate(), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const [settingsRes, statusRes] = await Promise.all([
        fetch('/api/data-export/settings', { headers }),
        fetch('/api/data-export/status', { headers }),
      ]);
      const settingsData = await settingsRes.json().catch(() => ({}));
      const statusData = await statusRes.json().catch(() => ({}));
      if (!settingsRes.ok) throw new Error(settingsData.error || 'Не удалось загрузить настройки');
      if (!statusRes.ok) throw new Error(statusData.error || 'Не удалось загрузить статус');
      setForm(
        normalizeDataExportSettings({
          ...settingsData,
          last_export_at: statusData.last_export_at ?? settingsData.last_export_at,
          next_export_at: statusData.next_export_at ?? settingsData.next_export_at,
          interval_days: statusData.interval_days ?? settingsData.interval_days,
        }),
      );
    } catch (e) {
      setError(e.message);
      setTimeout(() => setError(''), 4000);
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    if (isActive) load();
  }, [isActive, load]);

  useEffect(() => {
    if (!isActive || !form.next_export_at) return undefined;
    const t = setInterval(load, 60 * 1000);
    return () => clearInterval(t);
  }, [isActive, form.next_export_at, load]);

  const setNextFromParts = (date, time) => {
    setForm((prev) => ({
      ...prev,
      next_export_at: toIsoMsk(date, time),
    }));
  };

  const toggleItem = (key) => {
    setForm((prev) => ({
      ...prev,
      items: { ...prev.items, [key]: !prev.items[key] },
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = normalizeDataExportSettings({
        ...form,
        next_export_at: form.next_export_at,
      });
      const res = await fetch('/api/data-export/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          interval_days: payload.interval_days,
          next_export_at: payload.next_export_at,
          items: payload.items,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не удалось сохранить');
      setForm(normalizeDataExportSettings(data));
      setSuccess('Сохранено');
      setTimeout(() => setSuccess(''), 1800);
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 4000);
    } finally {
      setSaving(false);
    }
  };

  const exportNow = async () => {
    if (!isAdmin || exporting) return;
    setExporting(true);
    try {
      const res = await fetch('/api/data-export/now', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Ошибка выгрузки');
      setSuccess('Выгрузка в Telegram выполнена');
      setTimeout(() => setSuccess(''), 3000);
      await load();
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setExporting(false);
    }
  };

  if (!isActive) return null;
  if (loading) return <div className="loading">Загрузка…</div>;

  const countdown = formatCountdown(form.next_export_at);

  return (
    <form className="settings-panel" onSubmit={save}>
      <section className="settings-card">
        <div className="settings-card__head">
          <h2 className="settings-card__title">Расписание</h2>
          {isAdmin && (
            <button
              type="button"
              className="settings-btn settings-btn--ghost settings-card__action"
              onClick={exportNow}
              disabled={exporting}
            >
              {exporting ? '…' : 'Выгрузить сейчас'}
            </button>
          )}
        </div>
        <div className="settings-rows">
          <div className="settings-row settings-row--picker">
            <span className="settings-row__label">Дата (МСК)</span>
            <div className="settings-row__control">
              <DatePicker
                selectedDate={scheduleParts.date || minDate}
                minDate={minDate}
                onDateChange={(date) => {
                  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
                  setNextFromParts(date, scheduleParts.time || '12:00');
                }}
              />
            </div>
          </div>
          <div className="settings-row settings-row--picker">
            <span className="settings-row__label">Время (МСК)</span>
            <div className="settings-row__control">
              <TimePicker
                value={scheduleParts.time || '12:00'}
                onChange={(time) => {
                  setNextFromParts(scheduleParts.date || minDate, time);
                }}
              />
            </div>
          </div>
          <label className="settings-row">
            <span className="settings-row__label">Повтор (дни)</span>
            <input
              type="number"
              className="settings-row__input"
              min={1}
              max={90}
              value={form.interval_days}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  interval_days: e.target.value,
                }))
              }
            />
          </label>
          <div className="settings-row">
            <span className="settings-row__label">Последняя</span>
            <span className="settings-row__value">{formatDateTime(form.last_export_at)}</span>
          </div>
          <div className="settings-row">
            <span className="settings-row__label">Следующая</span>
            <span className="settings-row__value">
              {form.next_export_at
                ? `${formatDateTime(form.next_export_at)}${countdown ? ` · ${countdown}` : ''}`
                : 'Не задана'}
            </span>
          </div>
        </div>
      </section>

      <section className="settings-card">
        <h2 className="settings-card__title">Пункты выгрузки</h2>
        <div className="settings-rows settings-rows--toggles">
          {EXPORT_ITEM_KEYS.map((key) => (
            <SettingsToggle
              key={key}
              label={EXPORT_ITEM_LABELS[key]}
              description={EXPORT_ITEM_DESCRIPTIONS[key]}
              checked={Boolean(form.items[key])}
              onChange={() => toggleItem(key)}
            />
          ))}
        </div>
      </section>

      <div className="settings-actions">
        <button type="button" className="settings-btn settings-btn--ghost" onClick={load} disabled={saving}>
          Отменить
        </button>
        <button type="submit" className="settings-btn settings-btn--primary" disabled={saving}>
          {saving ? '…' : 'Сохранить'}
        </button>
      </div>
    </form>
  );
}
